import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EnvService, ValueLabel } from 'gematik-form-library';

export interface KpiChartConfigurationDto {
  id: string;
  chartType: KpiChartType;
  process: string;
  displayType: KpiChartDisplayType;
  groupBy: KpiChartGroupBy;
  timeDisplayUnit: KpiChartTimeDisplayUnit;
  filterBy: KpiChartFilterBy;
  filterValue: string;
  showOnDashboard: boolean;
  endpointUrl: string;
  title: string;
  data: KpiChartDataDto[];
  timePreset: KpiChartTimePresets;
  customTimeFrom: Date;
  customTimeTo: Date;
  sortBy: KpiChartSortBy;
  sortDirection: KpiChartSortDirection;
  color: string;
  sortIndex: number;
  hideOutlier: boolean;
  lowerPercentile: number;
  upperPercentile: number;
  unlimited: boolean;
}

export interface KpiBaseChartDto {
  id: string;
  chartType: KpiChartType;
  displayType: KpiChartDisplayType;
  timeDisplayUnit: KpiChartTimeDisplayUnit;
  title: string;
  data: KpiChartDataDto[];
  showOnDashboard: boolean;
  sortIndex: number;
  endpointUrl: string;
  sqlQuery: string;
  lastExecuted: Date;
}

export class KpiDynamicChartDto implements KpiBaseChartDto {
  id: string;
  chartType: KpiChartType;
  process: string;
  title: string;
  data: KpiChartDataDto[];
  showOnDashboard: boolean;
  sortIndex: number;
  endpointUrl: string;
  displayType: KpiChartDisplayType;
  groupBy: KpiChartGroupBy;
  timeDisplayUnit: KpiChartTimeDisplayUnit;
  filterBy: KpiChartFilterBy;
  filterValue: string;
  filterValueDisplay: string;
  timePreset: KpiChartTimePresets;
  customTimeFrom: Date;
  customTimeTo: Date;
  sortBy: KpiChartSortBy;
  sortDirection: KpiChartSortDirection;
  color: string;
  sqlQuery: string;
  lastExecuted: Date;
  hideOutlier: boolean;
  lowerPercentile: number;
  upperPercentile: number;
}

export class KpiStaticChartDto implements KpiBaseChartDto {
  id: string;
  chartType: KpiChartType;
  displayType: KpiChartDisplayType;
  timeDisplayUnit: KpiChartTimeDisplayUnit;
  data: KpiChartDataDto[];
  showOnDashboard: boolean;
  title: string;
  sortIndex: number;
  endpointUrl: string;
  sqlQuery: string;
  lastExecuted: Date;
}

export enum KpiChartType {
  NumberOfMyServicesUsersSummary = 'NumberOfMyServicesUsersSummary',
  NumberOfMyServicesUsersGrowth = 'NumberOfMyServicesUsersGrowth',
  ProcessingTime = 'ProcessingTime',
  ProcessingTimeSuccessful = 'ProcessingTimeSuccessful',
  ProcessingTimeUnsuccessful = 'ProcessingTimeUnsuccessful',
  NumberOfProcessInstances = 'NumberOfProcessInstances',
  NumberOfCorrections = 'NumberOfCorrections',
  NumberOfRejections = 'NumberOfRejections',
  NumberOfApprovals = 'NumberOfApprovals',
  NumberOfProcessTerminations = 'NumberOfProcessTerminations',
  NumberOfTechnicalMalfunctions = 'NumberOfTechnicalMalfunctions',
  NumberOfSuccessfulCompletions = 'NumberOfSuccessfulCompletions',
  NumberOfUnsuccessfulCompletions = 'NumberOfUnsuccessfulCompletions',
}

export enum KpiChartDisplayType {
  number = 'number',
  pie = 'pie',
  bar = 'bar',
  line = 'line',
}

export enum KpiChartGroupBy {
  date = 'date',
  month = 'month',
  quarter = 'quarter',
  year = 'year',
  organization_id = 'organization_id',
  organization_role_id = 'organization_role_id',
  process_variant = 'process_variant',
}

export enum KpiChartFilterBy {
  process,
  organization_id,
  organization_role_id,
  process_variant,
}

export enum KpiChartTimePresets {
  lastWeek,
  lastMonth,
  lastYear,
  custom,
}

export enum KpiChartTimeDisplayUnit {
  seconds,
  minutes,
  hours,
  days,
}

export enum KpiChartSortBy {
  value,
  label,
}

export enum KpiChartSortDirection {
  asc,
  desc,
}

export interface KpiChartDataDto {
  label: string;
  value: number;
  runningTotal: number;
  processKey?: string;
}

export interface GetFilterByValuesRequest {
  endpointUrl: string;
}

export interface ChartGenericRequest {
  endpointUrl: string;
}

export interface KpiChartSortOrderDto {
  id: string;
  sortIndex: number;
  endpointUrl: string;
}

export interface KpiChartFilterDto {
  process: string;
  filterBy: KpiChartFilterBy;
  timePreset: KpiChartTimePresets;
  customTimeFrom: Date;
  customTimeTo: Date;
  endpointUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class KpiChartService {
  constructor(
    private http: HttpClient,
    private env: EnvService,
  ) {}

  getFilterByProcessValues(payload: KpiChartFilterDto): Observable<ValueLabel[]> {
    const url = `${this.env.backendUrl}/charts/filter-by/process`;
    return this.http.post<ValueLabel[]>(url, payload);
  }

  getFilterByValues(payload: KpiChartFilterDto): Observable<ValueLabel[]> {
    const url = `${this.env.backendUrl}/charts/filter-by`;
    return this.http.post<ValueLabel[]>(url, payload);
  }

  renderChart(payload: KpiChartConfigurationDto, frequency: string = null): Observable<KpiBaseChartDto> {
    const url = `${this.env.backendUrl}/charts/render`;
    let params = new HttpParams();
    if (frequency) {
      params = params.set('frequency', frequency);
    }
    return this.http.post<KpiBaseChartDto>(url, payload, { params });
  }

  createChart(payload: KpiChartConfigurationDto): Observable<KpiBaseChartDto> {
    return this.http.post<KpiBaseChartDto>(`${this.env.backendUrl}/charts`, payload);
  }

  updateChart(id: string, payload: KpiChartConfigurationDto): Observable<KpiBaseChartDto> {
    return this.http.put<KpiBaseChartDto>(`${this.env.backendUrl}/charts/${id}`, payload);
  }

  deleteChart(id: string, endpointUrl: string): Observable<KpiBaseChartDto> {
    const payload: ChartGenericRequest = {
      endpointUrl,
    };
    return this.http.delete<KpiBaseChartDto>(`${this.env.backendUrl}/charts/${id}`, {
      body: payload,
    });
  }

  renderCharts(): Observable<KpiBaseChartDto[]> {
    return this.http.get<KpiBaseChartDto[]>(`${this.env.backendUrl}/charts/render`);
  }

  updateSortIndex(charts: KpiDynamicChartDto[]): Observable<KpiBaseChartDto[]> {
    const payload = charts.map(chart => ({
      id: chart.id,
      sortIndex: chart.sortIndex,
      endpointUrl: chart.endpointUrl,
    }));
    return this.http.post<KpiBaseChartDto[]>(`${this.env.backendUrl}/charts/sort-index`, payload);
  }

  getStaticChart(id: string, frequency: string): Observable<KpiBaseChartDto> {
    let params = new HttpParams();
    if (frequency) {
      params = params.set('frequency', frequency);
    }
    return this.http.get<KpiBaseChartDto>(`${this.env.backendUrl}/charts/static/${id}`, {
      params,
    });
  }
}
